import './scanner.css'
import '@/components/nfc/reader.css'

import React, { useEffect, useState } from 'react'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'
import { MainPageLayout } from '@/components/layout/MainLayout'
import { BackButton } from '@/components/layout/NavBar'
import { useNavigate } from 'react-router-dom'
import { openAppUrl } from '@/common/reactnav'

export function isNFCSupported(): boolean {
  return 'NDEFReader' in window
}

interface Props {
  session: CurrentSession
  services: Services
}

enum NFCScannerStatus {
  starting,
  ready,
  unsupported,
  readError,
  invalidTag,
}

function decodeRecordText(record: NDEFRecord): string | null {
  if (!record.data) {
    return null
  }

  try {
    const decoder = new TextDecoder(record.encoding || 'utf-8')
    return decoder.decode(record.data)
  } catch {
    return null
  }
}

function readURLFromMessage(message: NDEFMessage): string | null {
  for (const record of message.records) {
    if (record.recordType === 'url' || record.recordType === 'absolute-url') {
      return decodeRecordText(record)
    }

    if (record.recordType === 'text') {
      const text = decodeRecordText(record)?.trim()
      if (text && /^https?:\/\//i.test(text)) {
        return text
      }
    }
  }

  return null
}

export const UniversalNFCScannerBody: React.FC<Props> = ({ session }) => {
  const t = session.translates
  const [status, setStatus] = useState<NFCScannerStatus>(NFCScannerStatus.starting)
  const navigate = useNavigate()

  useEffect(() => {
    if (!('NDEFReader' in window)) {
      setStatus(NFCScannerStatus.unsupported)
      return
    }

    let aborted = false
    const ndef = new NDEFReader()
    const abortController = new AbortController()

    const onReading = (event: Event) => {
      if (aborted) {
        return
      }

      const { message } = event as NDEFMessageEvent
      const tagURL = readURLFromMessage(message)

      if (!tagURL) {
        setStatus(NFCScannerStatus.invalidTag)
        return
      }

      if (openAppUrl(navigate, tagURL)) {
        aborted = true
        return
      }

      setStatus(NFCScannerStatus.invalidTag)
    }

    const onReadingError = () => {
      if (!aborted) {
        setStatus(NFCScannerStatus.readError)
      }
    }

    ndef.scan({ signal: abortController.signal }).then(
      () => {
        if (aborted) {
          return
        }
        ndef.addEventListener('reading', onReading)
        ndef.addEventListener('readingerror', onReadingError)
        setStatus(NFCScannerStatus.ready)
      },
      () => {
        if (!aborted) {
          setStatus(NFCScannerStatus.unsupported)
        }
      }
    )

    return () => {
      aborted = true
      abortController.abort()
      ndef.removeEventListener('reading', onReading)
      ndef.removeEventListener('readingerror', onReadingError)
    }
  }, [])

  const message = (() => {
    switch (status) {
      case NFCScannerStatus.ready:
        return t.for('page.nfc-scanner.ready')
      case NFCScannerStatus.unsupported:
        return t.for('page.nfc-scanner.unsupported')
      case NFCScannerStatus.readError:
        return t.for('page.nfc-scanner.read_error')
      case NFCScannerStatus.invalidTag:
        return t.for('page.nfc-scanner.invalid_tag')
      default:
        return t.for('page.nfc-scanner.starting')
    }
  })()

  return (
    <div className="nfc-scanner-page">
      <div className="nfcreader-popup">
        <div className='icon-box'>
          <i className='svgfont-nfc' />
        </div>
        <div className="status">{message}</div>
      </div>
    </div>
  )
}

export const UniversalNFCScannerPage: React.FC<Props> = (props) => {
  const { services, session } = props

  return (
    <MainPageLayout
      pageName='nfc-scanner'
      {...props}
      translates={props.session.translates}
      navbarLeft={<BackButton services={services} translates={session.translates} />}>
      <UniversalNFCScannerBody {...props} />
    </MainPageLayout>
  )
}
